// frontend/src/api/leads.js
// Updated: March 27, 2025

import { apiClient, handleError } from './index';

/**
 * Generate a payment URL for a lead
 * @param {number} leadId - The ID of the lead
 * @param {number} customPrice - Optional custom price (defaults to calculated price if not provided)
 * @returns {Promise} API response with payment URL
 */
export const generatePaymentUrl = async (leadId, customPrice = null) => {
  try {
    console.log(`Requesting payment URL for lead ${leadId}${customPrice ? ' with custom price: ' + customPrice : ''}`);
    
    // FIXED: Remove '/api' prefix from the URL
    const response = await apiClient.post(`/admin/leads/${leadId}/payment-url`, { 
      price: customPrice
    });
    
    console.log('Payment URL response:', response.data);
    
    if (response.data.success) {
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || 'Payment URL generated successfully'
      };
    } else {
      return {
        success: false,
        message: response.data.message || 'Failed to generate payment link'
      };
    }
  } catch (error) {
    console.error('Error generating payment URL:', error);
    return handleError(error);
  }
};

/**
 * Get detailed information about a specific lead
 * @param {number} leadId - The ID of the lead
 * @returns {Promise} API response with lead details
 */
export const getLeadDetails = async (leadId) => {
  try {
    console.log('Fetching lead details for ID:', leadId);
    const response = await apiClient.get(`/admin/leads/${leadId}`);
    const data = response.data;
    
    console.log('Raw lead details response:', response.data);
    
    // Ensure consistent field names
    if (data.data) {
      data.data.zip_code = data.data.zip_code || data.data.zipCode || data.data.postal_code;
      
      // Apply default price logic
      if (!data.data.price) {
        if (!data.data.investment_amount || data.data.investment_amount <= 0) {
          data.data.price = 150; // Default price when investment amount is not specified
        } else {
          data.data.price = 50; // Base price when investment amount is specified
        }
      }
    }
    
    return {
      success: true,
      data: data.data || data,
      message: data.message || 'Lead details retrieved successfully'
    };
  } catch (error) {
    console.error('Error fetching lead details:', error);
    return handleError(error);
  }
};

/**
 * Get city and state from zip code
 * @param {string} zipCode - The zip code to look up
 * @returns {Promise} API response with city and state
 */
export const getLocationFromZip = async (zipCode) => {
  try {
    console.log('Looking up location for ZIP:', zipCode);
    
    if (!zipCode || zipCode.length < 5) {
      console.error('Invalid ZIP code provided:', zipCode);
      return {
        success: false,
        message: 'Invalid ZIP code provided',
        error: 'ZIP code must be at least 5 digits'
      };
    }

    // First try zippopotam.us API
    try {
      console.log('Attempting zippopotam.us lookup...');
      const response = await apiClient.get(`/utils/location/${zipCode}`);
      console.log('Location API response:', response.data);
      
      if (response.data && response.data.success && response.data.data) {
        return {
          success: true,
          data: {
            city: response.data.data.city,
            state: response.data.data.state
          },
          message: 'Location found successfully'
        };
      } else {
        throw new Error('Invalid response from location service');
      }
    } catch (zipError) {
      console.error('Error with zippopotam.us lookup:', zipError);
      
      // Try Census API as fallback
      try {
        console.log('Attempting Census API lookup...');
        const censusResponse = await apiClient.get(`/utils/location/census/${zipCode}`);
        console.log('Census API response:', censusResponse.data);
        
        if (censusResponse.data && censusResponse.data.success && censusResponse.data.data) {
          return {
            success: true,
            data: {
              city: censusResponse.data.data.city,
              state: censusResponse.data.data.state
            },
            message: 'Location found successfully (Census API)'
          };
        } else {
          throw new Error('Invalid response from Census API');
        }
      } catch (censusError) {
        console.error('Error with Census API lookup:', censusError);
        throw new Error('Both location services failed');
      }
    }
  } catch (error) {
    console.error('Error getting location from ZIP:', error);
    return {
      success: false,
      message: error.message || 'Failed to get location from ZIP code',
      error: error.message
    };
  }
};

/**
 * Update lead information including state and city
 * @param {number} leadId - The ID of the lead
 * @param {Object} updateData - The data to update
 * @returns {Promise} API response
 */
export const updateLead = async (leadId, updateData) => {
  try {
    console.log('Updating lead:', leadId, 'with data:', updateData);

    // Validate state code if it's being updated
    if (updateData.state) {
      const validStates = [
        'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
        'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
        'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
        'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
        'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'
      ];
      
      if (!validStates.includes(updateData.state)) {
        console.error('Invalid state code:', updateData.state);
        return {
          success: false,
          message: `Invalid state code: ${updateData.state}`,
          error: 'State code must be a valid two-letter US state code'
        };
      }
    }

    const response = await apiClient.put(`/admin/leads/${leadId}`, updateData);
    console.log('Update response:', response.data);

    if (response.data.success) {
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || 'Lead updated successfully'
      };
    } else {
      console.error('Update failed:', response.data);
      return {
        success: false,
        message: response.data.message || 'Failed to update lead',
        error: response.data.error
      };
    }
  } catch (error) {
    console.error('Error updating lead:', error);
    return {
      success: false,
      message: error.message || 'An error occurred while updating the lead',
      error: error.message
    };
  }
};

/**
 * Get available leads for a state
 * @param {string} state - The state code
 * @returns {Promise<Object>} Available leads
 */
export const getAvailableLeads = async (state) => {
  try {
    const response = await apiClient.get('/leads/available', {
      params: { state }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching available leads:', error);
    throw error;
  }
};

/**
 * Get purchased leads
 * @returns {Promise<Object>} Purchased leads
 */
export const getPurchasedLeads = async () => {
  try {
    const response = await apiClient.get('/leads/purchased');
    return response.data;
  } catch (error) {
    console.error('Error fetching purchased leads:', error);
    throw error;
  }
}; 